import { useLayoutEffect, useState, type RefObject } from 'react';
import { isReadOnlyView } from '../boot/view-handoff';
import { bt, btp } from '../i18n';
import { useBuilderStore } from '../state/builderStore';
import { useChromeStore } from '../state/chromeStore';
import { useGenerationStore } from '../state/generation/generationStore';
import { CanvasFrame, blockLabel, fieldLabel } from './CanvasFrame';
import { sectionDisplayName } from '../shell/sectionVariantLabel';
import { AskChangesPopover } from './AskChangesPopover';
import { SparkleIcon } from '../ui/icons';

/**
 * The canvas SELECTION FRAME — a measured overlay that draws a labelled frame around the selected child
 * (a block, or a field hotspot) the way Shopify frames a selected block. A bare SECTION keeps
 * `EditableSection`'s own outline, so we only frame children here.
 *
 * The measuring + painting lives in <CanvasFrame> (shared with <HoverFrame>); this component only resolves
 * WHICH element the current selection points at, and its label.
 *
 * IL PORTE AUSSI « DEMANDER UN CHANGEMENT » (27/08). La pastille était ancrée en haut de la SECTION :
 * sur une fiche produit haute de trois écrans, sélectionner un bloc du bas la laissait hors de vue —
 * « je vois pas ask for changes, elle doit toujours être près du composant sélectionné ». Le cadre,
 * lui, est par construction sur l'élément visé : le bouton et sa bulle voyagent donc avec lui.
 */
export function SelectionFrame({ pageRef }: { pageRef: RefObject<HTMLDivElement | null> }) {
  const selectedSectionId = useBuilderStore((s) => s.selectedSectionId);
  const selectedBlockId = useBuilderStore((s) => s.selectedBlockId);
  const focusedFieldId = useBuilderStore((s) => s.focusedFieldId);
  const sectionsById = useBuilderStore((s) => s.sectionsById);
  const previewMode = useBuilderStore((s) => s.previewMode);

  const [target, setTarget] = useState<Element | null>(null);
  const [label, setLabel] = useState('');

  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page || previewMode) {
      setTarget(null);
      return;
    }
    const section = selectedSectionId ? sectionsById[selectedSectionId] : undefined;
    const esc = (v: string) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v);

    let el: Element | null = null;
    let lbl = '';
    if (focusedFieldId && selectedSectionId) {
      const sec = page.querySelector(`[data-section-id="${esc(selectedSectionId)}"]`);
      el = sec?.querySelector(`[data-aqx-field-id="${esc(focusedFieldId)}"]`) ?? null;
      lbl = fieldLabel(section, focusedFieldId);
    } else if (selectedBlockId) {
      el = page.querySelector(`[data-aqx-block-id="${esc(selectedBlockId)}"]`);
      lbl = blockLabel(section, selectedBlockId);
    } else if (selectedSectionId) {
      // LA SECTION AUSSI (fondateur, 27/08 : « look how it looks, I want it like this »). Elle avait
      // sa barre flottante posée EN HAUT À DROITE, par-dessus le titre du produit ; le bloc, lui, a
      // son étiquette accrochée au bord. On lui donne la même : son nom, et la pastille à côté.
      el = page.querySelector(`[data-section-id="${esc(selectedSectionId)}"]`);
      lbl = section ? sectionDisplayName(section) : '';
    }
    setTarget(el);
    setLabel(lbl);
  }, [selectedSectionId, selectedBlockId, focusedFieldId, sectionsById, previewMode, pageRef]);

  const askOpen = useChromeStore((s) => s.askChangesOpen);
  const setAskOpen = useChromeStore((s) => s.setAskChangesOpen);
  const busy = useGenerationStore((s) => s.regenSectionId === selectedSectionId);

  return (
    <CanvasFrame
      pageRef={pageRef}
      target={target}
      label={label}
      variant="selected"
      ring={!!selectedBlockId || !!focusedFieldId}
      action={
        // Consultation superadmin : « Demander une modification » réécrit la section — on ne la propose
        // pas du tout (fondatrice 25/10/2026).
        selectedSectionId && !isReadOnlyView() ? (
          <button
            type="button"
            className="bld-framebtn"
            aria-expanded={askOpen}
            aria-label={btp('chrome.ask.aria_named', 'Ask for a change — {name}', { name: label })}
            disabled={busy}
            onClick={() => setAskOpen(!askOpen)}
          >
            <span className="bld-framebtn__icon" aria-hidden="true">
              <SparkleIcon size={15} />
            </span>
            {busy
              ? bt('chrome.ask.regenerating', 'Regenerating…')
              : bt('chrome.ask.button', 'Ask for changes')}
          </button>
        ) : undefined
      }
      overlay={
        askOpen && selectedSectionId ? (
          <AskChangesPopover sectionId={selectedSectionId} onClose={() => setAskOpen(false)} />
        ) : undefined
      }
    />
  );
}
